import { ApiErrorEnum } from '../../enums/ApiErrorEnum';
import { getApiErrorCode } from '../apiErrorCode/apiErrorCode';

/** Parsed 403 `TOTP_SETUP_REQUIRED` body from `POST /user/auth/v2` (mandatory setup, no TOTP yet). */
export interface TotpSetupRequiredError {
  partialAuthToken: string;
  expiresAt: string;
  userEmail: string;
}

/** httpClient-normalized errors expose the API payload on `error.response` and the HTTP status on `error.status`. */
export function getTotpErrorResponse(error: unknown): unknown {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  return (error as { response?: unknown }).response;
}

/** HTTP status code from an httpClient-normalized error rejection. */
export function getTotpErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  return (error as { status?: number }).status;
}

/**
 * Parses a `403 TOTP_SETUP_REQUIRED` error thrown by `POST /user/auth/v2` into typed data,
 * or `null` when the error is not this specific case (e.g. wrong OTP, other HTTP errors).
 */
export function parseTotpSetupRequiredError(error: unknown): TotpSetupRequiredError | null {
  const responseBody = getTotpErrorResponse(error);
  const code = getApiErrorCode(responseBody);
  if (getTotpErrorStatus(error) !== 403 || code !== ApiErrorEnum.TotpSetupRequired) {
    return null;
  }

  const body = responseBody as Partial<TotpSetupRequiredError>;
  if (typeof body.partialAuthToken !== 'string') {
    return null;
  }

  return {
    partialAuthToken: body.partialAuthToken,
    expiresAt: typeof body.expiresAt === 'string' ? body.expiresAt : '',
    userEmail: typeof body.userEmail === 'string' ? body.userEmail : '',
  };
}

/**
 * True when a forced-setup API call (`/user/auth/totp/setup`, `/user/auth/totp/setup/verify`)
 * failed because the partial auth token is missing, invalid, or expired — the UI must restart
 * the login flow rather than let the user retry the current step.
 */
export function isTotpForcedSetupTokenInvalid(error: unknown): boolean {
  return getApiErrorCode(getTotpErrorResponse(error)) === ApiErrorEnum.InvalidToken;
}
